'use client';

import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Landmark, TrendingUp, TrendingDown, Filter, Search, ChevronRight, Bell, BellOff, Users } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { useTimezone } from '@/hooks/useTimezone';
import { useAuth } from '@/context/AuthContext';
import {
  getCongressFollows,
  followCongressMember,
  unfollowCongressMember,
  getPublicPortfolioSettings,
  type CongressFollow,
} from '@/lib/api-client';
import CongressionalDirectory from '@/components/congress/CongressionalDirectory';

interface CongressTrade {
  representative: string;
  bioGuideId: string;
  photoUrl: string;
  party: string;
  chamber: string;
  ticker: string | null;
  transaction: string;
  amount: string;
  range: string;
  transactionDate: string;
  reportDate: string;
  description: string | null;
}

interface TopTrader {
  representative: string;
  bioGuideId: string;
  photoUrl: string;
  party: string;
  chamber: string;
  tradeCount: number;
  totalAmount: number;
  lastTradeDate: string;
}

function partyColor(party: string) {
  if (party === 'D') return 'bg-blue-500';
  if (party === 'R') return 'bg-red-500';
  return 'bg-slate-500';
}

function partyLabel(party: string) {
  if (party === 'D') return 'Democrat';
  if (party === 'R') return 'Republican';
  if (party === 'I') return 'Independent';
  return party;
}

function isBuy(txn: string) {
  return txn.toLowerCase().includes('purchase');
}

function formatAmount(amount: string, range: string): string {
  const n = parseFloat(amount);
  if (n > 0) {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n.toLocaleString()}`;
  }
  return range || '—';
}

function formatDate(d: string, tz?: string): string {
  if (!d) return '—';
  try {
    return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz });
  } catch {
    return d;
  }
}

function MemberPhoto({ photoUrl, name, party, size = 40 }: { photoUrl: string; name: string; party: string; size?: number }) {
  const [failed, setFailed] = useState(false);
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2);

  if (!photoUrl || failed) {
    return (
      <div
        className={cn('rounded-full flex items-center justify-center text-white font-bold flex-shrink-0', partyColor(party))}
        style={{ width: size, height: size, fontSize: size * 0.35 }}
      >
        {initials}
      </div>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={photoUrl}
      alt={name}
      className="rounded-full object-cover flex-shrink-0 border-2"
      style={{ width: size, height: size, borderColor: party === 'D' ? '#3b82f6' : party === 'R' ? '#ef4444' : '#64748b' }}
      onError={() => setFailed(true)}
    />
  );
}

function FollowButton({
  bioGuideId,
  representative,
  isFollowed,
  onToggle,
  size = 'sm',
}: {
  bioGuideId: string;
  representative: string;
  isFollowed: boolean;
  onToggle: (bioGuideId: string, representative: string) => void;
  size?: 'sm' | 'md';
}) {
  const { user } = useAuth();
  const router = useRouter();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      router.push('/login');
      return;
    }
    onToggle(bioGuideId, representative);
  };

  const sizeClasses = size === 'sm'
    ? 'p-1.5 rounded-lg'
    : 'px-3 py-1.5 rounded-lg text-xs font-medium gap-1.5';

  return (
    <button
      onClick={handleClick}
      title={isFollowed ? 'Unfollow' : 'Follow for trade alerts'}
      className={cn(
        'inline-flex items-center transition-colors flex-shrink-0',
        sizeClasses,
        isFollowed
          ? 'bg-sky-500/15 text-sky-400 border border-sky-500/30 hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/30'
          : 'bg-card-hover-bg text-fg-muted border border-default hover:text-sky-400 hover:border-sky-500/30',
      )}
    >
      {isFollowed ? <Bell size={size === 'sm' ? 14 : 13} className="fill-current" /> : <BellOff size={size === 'sm' ? 14 : 13} />}
      {size === 'md' && <span>{isFollowed ? 'Following' : 'Follow'}</span>}
    </button>
  );
}

type FilterType = 'all' | 'buy' | 'sell';
type ChamberFilter = 'all' | 'House' | 'Senate';

export default function CongressTradingPage() {
  // Next.js needs useSearchParams() wrapped in Suspense for static export.
  return (
    <Suspense fallback={<div className="max-w-screen-xl mx-auto px-2 py-6 text-sm text-fg-subtle">Loading…</div>}>
      <CongressTradingPageInner />
    </Suspense>
  );
}

function CongressTradingPageInner() {
  const { user } = useAuth();
  const tz = useTimezone();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [trades, setTrades] = useState<CongressTrade[]>([]);
  const [topTraders, setTopTraders] = useState<TopTrader[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [chamberFilter, setChamberFilter] = useState<ChamberFilter>('all');
  const [search, setSearch] = useState('');
  const [follows, setFollows] = useState<CongressFollow[]>([]);
  const [followsLoading, setFollowsLoading] = useState(false);
  const [directoryEnabled, setDirectoryEnabled] = useState(false);

  // Directory tab visibility is gated by the portfolio admin toggle — same
  // switch that used to control the old /portfolio/congressional page.
  useEffect(() => {
    getPublicPortfolioSettings()
      .then((s) => setDirectoryEnabled(!!s.congressionalEnabled))
      .catch(() => setDirectoryEnabled(false));
  }, []);

  // Keep a local copy of the tab so clicks flip the view instantly,
  // independent of whatever delay the URL replace round-trip takes.
  const [activeTab, setActiveTabState] = useState<'trades' | 'directory'>('trades');

  // Sync FROM the URL (back/forward nav, direct deep-links).
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const next: 'trades' | 'directory' = tabParam === 'directory' && directoryEnabled ? 'directory' : 'trades';
    setActiveTabState(next);
  }, [searchParams, directoryEnabled]);

  const setActiveTab = (t: 'trades' | 'directory') => {
    setActiveTabState(t);
    const sp = new URLSearchParams(Array.from(searchParams.entries()));
    if (t === 'trades') sp.delete('tab'); else sp.set('tab', t);
    const qs = sp.toString();
    router.replace(qs ? `/congress?${qs}` : '/congress', { scroll: false });
  };

  const followedIds = useMemo(() => new Set(follows.map((f) => f.bioGuideId)), [follows]);

  // Fetch trades and top traders
  useEffect(() => {
    Promise.all([
      fetch('/api/congress/trades').then((r) => r.json()).then((r) => setTrades(r.data ?? [])),
      fetch('/api/congress/top-traders').then((r) => r.json()).then((r) => setTopTraders(r.data ?? [])),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Fetch user's follows when logged in
  useEffect(() => {
    if (!user) {
      setFollows([]);
      return;
    }
    setFollowsLoading(true);
    getCongressFollows()
      .then((data) => setFollows(data))
      .catch(() => setFollows([]))
      .finally(() => setFollowsLoading(false));
  }, [user]);

  const handleToggleFollow = useCallback(async (bioGuideId: string, representative: string) => {
    if (followedIds.has(bioGuideId)) {
      // Optimistically remove
      setFollows((prev) => prev.filter((f) => f.bioGuideId !== bioGuideId));
      try {
        await unfollowCongressMember(bioGuideId);
      } catch {
        // Re-add on error
        setFollows((prev) => [...prev, { id: '', bioGuideId, representative, createdAt: '' }]);
      }
    } else {
      // Optimistically add
      const tempFollow: CongressFollow = { id: 'temp', bioGuideId, representative, createdAt: new Date().toISOString() };
      setFollows((prev) => [...prev, tempFollow]);
      try {
        const result = await followCongressMember(bioGuideId, representative);
        setFollows((prev) => prev.map((f) => f.id === 'temp' && f.bioGuideId === bioGuideId ? result : f));
      } catch (err) {
        // Remove on error
        setFollows((prev) => prev.filter((f) => !(f.id === 'temp' && f.bioGuideId === bioGuideId)));
        const msg = err instanceof Error ? err.message : 'Failed to follow';
        alert(msg);
      }
    }
  }, [followedIds]);

  // Build a lookup of bioGuideId -> { representative, photoUrl, party } from trades + topTraders
  const memberInfo = useMemo(() => {
    const map = new Map<string, { representative: string; photoUrl: string; party: string }>();
    for (const t of trades) {
      if (t.bioGuideId && !map.has(t.bioGuideId)) {
        map.set(t.bioGuideId, { representative: t.representative, photoUrl: t.photoUrl, party: t.party });
      }
    }
    for (const t of topTraders) {
      if (t.bioGuideId && !map.has(t.bioGuideId)) {
        map.set(t.bioGuideId, { representative: t.representative, photoUrl: t.photoUrl, party: t.party });
      }
    }
    return map;
  }, [trades, topTraders]);

  const filtered = useMemo(() => {
    let items = trades;
    if (filter === 'buy') items = items.filter((t) => isBuy(t.transaction));
    if (filter === 'sell') items = items.filter((t) => !isBuy(t.transaction));
    if (chamberFilter !== 'all') items = items.filter((t) => t.chamber === chamberFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (t) => t.representative.toLowerCase().includes(q) || (t.ticker && t.ticker.toLowerCase().includes(q))
      );
    }
    // Sort by the most recent of (report_date, transaction_date). A trade
    // filed today for an old transaction is still "news" and should float
    // to the top alongside actually-recent transactions.
    return items.sort((a, b) => {
      const ka = (a.reportDate || '') > (a.transactionDate || '') ? (a.reportDate || '') : (a.transactionDate || '');
      const kb = (b.reportDate || '') > (b.transactionDate || '') ? (b.reportDate || '') : (b.transactionDate || '');
      return kb.localeCompare(ka);
    });
  }, [trades, filter, chamberFilter, search]);

  return (
    <div className="max-w-screen-xl mx-auto px-2 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Landmark size={24} className="text-sky-400" />
          <h1 className="text-2xl font-bold text-white">Congressional Trading</h1>
        </div>
        <p className="text-fg-muted text-sm">Recent stock trades disclosed by members of the U.S. Congress under the STOCK Act</p>
      </div>

      {/* Tabs — Directory is admin-gated */}
      {directoryEnabled && (
        <div className="flex items-center gap-1 mb-5 border-b border-default">
          <button
            onClick={() => setActiveTab('trades')}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'trades' ? 'text-sky-400 border-sky-400' : 'text-fg-muted border-transparent hover:text-slate-200',
            )}
          >
            <TrendingUp size={14} /> Trades
          </button>
          <button
            onClick={() => setActiveTab('directory')}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'directory' ? 'text-sky-400 border-sky-400' : 'text-fg-muted border-transparent hover:text-slate-200',
            )}
          >
            <Users size={14} /> Directory
          </button>
        </div>
      )}

      {activeTab === 'directory' && directoryEnabled ? (
        <CongressionalDirectory returnTo="/congress?tab=directory" />
      ) : (
        <CongressTradesView
          user={user}
          follows={follows}
          memberInfo={memberInfo}
          filter={filter}
          setFilter={setFilter}
          chamberFilter={chamberFilter}
          setChamberFilter={setChamberFilter}
          search={search}
          setSearch={setSearch}
          filtered={filtered}
          loading={loading}
          topTraders={topTraders}
          followsLoading={followsLoading}
          followedIds={followedIds}
          handleToggleFollow={handleToggleFollow}
          tz={tz}
        />
      )}
    </div>
  );
}

interface CongressTradesViewProps {
  user: ReturnType<typeof useAuth>['user'];
  follows: CongressFollow[];
  memberInfo: Map<string, { representative: string; photoUrl: string; party: string }>;
  filter: FilterType;
  setFilter: (v: FilterType) => void;
  chamberFilter: ChamberFilter;
  setChamberFilter: (v: ChamberFilter) => void;
  search: string;
  setSearch: (v: string) => void;
  filtered: CongressTrade[];
  loading: boolean;
  topTraders: TopTrader[];
  followsLoading: boolean;
  followedIds: Set<string>;
  handleToggleFollow: (bioGuideId: string, representative: string) => Promise<void>;
  tz: string;
}

function CongressTradesView({ user, follows, memberInfo, filter, setFilter, chamberFilter, setChamberFilter, search, setSearch, filtered, loading, topTraders, followsLoading, followedIds, handleToggleFollow, tz }: CongressTradesViewProps) {
  return (
    <>
      {/* Your Follows Section */}
      {user && follows.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-wide mb-3">
            <Bell size={13} className="inline mr-1.5 text-sky-400" />
            Your Follows ({follows.length})
          </h2>
          <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-2">
            {follows.map((follow) => {
              const info = memberInfo.get(follow.bioGuideId);
              const photoUrl = info?.photoUrl || '';
              const party = info?.party || '';
              const id = follow.bioGuideId;
              const letter = id.charAt(0) || 'X';
              const fallbackPhoto = id ? `https://bioguide.congress.gov/bioguide/photo/${letter}/${id}.jpg` : '';
              return (
                <div
                  key={follow.bioGuideId}
                  className="flex items-center gap-2 px-3 py-2 bg-sky-500/5 border border-sky-500/20 rounded-xl min-w-fit flex-shrink-0"
                >
                  <MemberPhoto photoUrl={photoUrl || fallbackPhoto} name={follow.representative} party={party} size={32} />
                  <span className="text-xs font-medium text-white whitespace-nowrap">{follow.representative}</span>
                  <button
                    onClick={() => handleToggleFollow(follow.bioGuideId, follow.representative)}
                    className="p-1 text-sky-400 hover:text-red-400 transition-colors"
                    title="Unfollow"
                  >
                    <Bell size={12} className="fill-current" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Traders Row */}
      {topTraders.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-wide mb-3">Most Active Traders</h2>
          <div className="flex gap-3 overflow-x-auto scrollbar-thin pb-2">
            {topTraders.slice(0, 10).map((trader) => (
              <Link
                key={trader.bioGuideId || trader.representative}
                href={`/congress/member/${trader.bioGuideId}?name=${encodeURIComponent(trader.representative)}&party=${encodeURIComponent(trader.party)}&chamber=${encodeURIComponent(trader.chamber)}`}
                className={cn(
                  'flex flex-col items-center gap-2 px-4 py-3 bg-card-bg border border-default rounded-xl min-w-[120px] flex-shrink-0 hover:bg-card-hover-bg hover:border-muted transition-all relative',
                )}
              >
                <div className="absolute top-1.5 right-1.5">
                  <FollowButton
                    bioGuideId={trader.bioGuideId}
                    representative={trader.representative}
                    isFollowed={followedIds.has(trader.bioGuideId)}
                    onToggle={handleToggleFollow}
                    size="sm"
                  />
                </div>
                <MemberPhoto photoUrl={trader.photoUrl} name={trader.representative} party={trader.party} size={48} />
                <div className="text-center">
                  <p className="text-xs font-medium text-white leading-tight">{trader.representative.split(' ').slice(-1)[0]}</p>
                  <p className="text-[10px] text-fg-subtle">{trader.chamber}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <div className={cn('w-1.5 h-1.5 rounded-full', partyColor(trader.party))} />
                    <span className="text-[10px] text-fg-muted">{trader.tradeCount} trades</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or ticker..."
            className="w-full bg-card-bg border border-default rounded-lg pl-9 pr-4 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-sky-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-subtle hover:text-white text-xs">Clear</button>
          )}
        </div>
        <div className="flex gap-1">
          {([['all', 'All'], ['buy', 'Buys'], ['sell', 'Sells']] as const).map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              className={cn(
                'px-3 py-2 text-xs font-medium rounded-lg transition-colors',
                filter === val ? 'bg-sky-500/15 text-sky-400 border border-sky-500/30' : 'bg-card-bg border border-default text-fg-muted hover:text-white'
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {([['all', 'Both'], ['House', 'House'], ['Senate', 'Senate']] as const).map(([val, label]) => (
            <button
              key={val}
              onClick={() => setChamberFilter(val as ChamberFilter)}
              className={cn(
                'px-3 py-2 text-xs font-medium rounded-lg transition-colors',
                chamberFilter === val ? 'bg-sky-500/15 text-sky-400 border border-sky-500/30' : 'bg-card-bg border border-default text-fg-muted hover:text-white'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-fg-subtle mb-4">
        {loading ? 'Loading trades...' : <><span className="text-white">{filtered.length}</span> trades</>}
      </p>

      {/* Trades List */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-card-bg border border-default rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-card-hover-bg" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-card-hover-bg rounded w-1/3" />
                  <div className="h-3 bg-card-hover-bg rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-fg-subtle">No trades found matching your filters.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((trade, i) => {
            const buy = isBuy(trade.transaction);
            return (
              <div
                key={`${trade.bioGuideId}-${trade.ticker}-${trade.transactionDate}-${i}`}
                className="bg-card-bg border border-default rounded-xl p-4 hover:bg-card-hover-bg transition-colors"
              >
                <div className="flex items-start gap-3">
                  {/* Photo */}
                  <Link href={`/congress/member/${trade.bioGuideId}?name=${encodeURIComponent(trade.representative)}&party=${encodeURIComponent(trade.party)}&chamber=${encodeURIComponent(trade.chamber)}`}>
                    <MemberPhoto photoUrl={trade.photoUrl} name={trade.representative} party={trade.party} />
                  </Link>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link href={`/congress/member/${trade.bioGuideId}?name=${encodeURIComponent(trade.representative)}&party=${encodeURIComponent(trade.party)}&chamber=${encodeURIComponent(trade.chamber)}`} className="text-sm font-semibold text-white hover:text-sky-400 transition-colors">
                        {trade.representative}
                      </Link>
                      <div className={cn('w-2 h-2 rounded-full', partyColor(trade.party))} title={partyLabel(trade.party)} />
                      <span className="text-[10px] text-fg-subtle bg-card-hover-bg px-1.5 py-0.5 rounded">{trade.chamber}</span>
                    </div>

                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {/* Transaction type badge */}
                      <span className={cn(
                        'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded',
                        buy ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                      )}>
                        {buy ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                        {buy ? 'Buy' : 'Sell'}
                      </span>

                      {/* Ticker */}
                      {trade.ticker && (
                        <Link
                          href={`/stocks/${trade.ticker}`}
                          className="text-xs font-mono font-semibold text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded hover:bg-sky-500/20 transition-colors"
                        >
                          {trade.ticker}
                        </Link>
                      )}

                      {/* Amount */}
                      <span className="text-xs text-fg-muted font-mono">{formatAmount(trade.amount, trade.range)}</span>
                    </div>

                    {trade.description && (
                      <p className="text-[11px] text-fg-subtle mt-1 truncate">{trade.description}</p>
                    )}
                  </div>

                  {/* Follow button + Date */}
                  <div className="flex items-start gap-2 flex-shrink-0">
                    {trade.bioGuideId && (
                      <FollowButton
                        bioGuideId={trade.bioGuideId}
                        representative={trade.representative}
                        isFollowed={followedIds.has(trade.bioGuideId)}
                        onToggle={handleToggleFollow}
                        size="sm"
                      />
                    )}
                    <div className="text-right hidden sm:block">
                      <div className="text-xs text-fg-muted">{formatDate(trade.transactionDate, tz)}</div>
                      <div className="text-[10px] text-slate-600 mt-0.5">Filed {formatDate(trade.reportDate, tz)}</div>
                    </div>
                  </div>
                </div>

                {/* Mobile date */}
                <div className="sm:hidden mt-2 text-[11px] text-fg-subtle">
                  {formatDate(trade.transactionDate, tz)} · Filed {formatDate(trade.reportDate, tz)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
